import { observer } from 'mobx-react-lite';
import { ApplicationSdk } from '../../../core/sdk';
import { useEffect } from 'react';
import DocumentStore from '../stores/DocumentStore';
import { ProgressSpinner } from 'primereact/progressspinner';
import MatenSign from '../../../shared/components/MatenSign';
import MatenError from '../../../shared/components/MatenError';
import MatenSuccess from '../../../shared/components/MatenSuccess';

const DocumentView = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(DocumentStore);
    }, [sdk]);

    const documentStore: DocumentStore | undefined = sdk.currentStore;
    const cleanHtml =
        documentStore?.documentHtml?.replace(/\n\s*\n/g, '\n').replace(/^\s+|\s+$/g, '') || '';

    if (!documentStore) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '50vh',
                    flexDirection: 'column',
                    gap: '1rem',
                }}
            >
                <ProgressSpinner
                    style={{
                        width: '50px',
                        height: '50px',
                    }}
                    strokeWidth="4"
                    animationDuration="1s"
                />
                <div
                    style={{
                        color: '#6c757d',
                        fontSize: '1rem',
                        textAlign: 'center',
                    }}
                >
                    Store wird initialisiert...
                </div>
            </div>
        );
    }

    if (documentStore.documentSigned) {
        return (
            <MatenSuccess
                title="Dokument erfolgreich signiert"
                message="Ihr Dokument wurde erfolgreich unterzeichnet."
                hint="Sie können das Fenster nun schließen."
                showButton={!!documentStore.documentButton}
                buttonLabel={documentStore.documentButton?.label || 'Ok'}
                buttonIcon={`pi ${documentStore.documentButton?.icon || 'pi-check'}`}
                targetBlank={documentStore.documentButton?.target_blank || false}
                buttonLink={documentStore.documentButton?.target_url}
                minHeight="400px"
                testId="document-signed-success"
            />
        );
    }

    // If there was an error return with MatenError
    if (documentStore.documentError) {
        return (
            <MatenError
                title="Fehler"
                message={documentStore.outputDocumentError}
                errorType="error"
                showButton={true}
                buttonLabel="Erneut versuchen"
                buttonIcon="pi pi-refresh"
                onButtonClick={() => {
                    documentStore.documentError = null;
                    documentStore.loadDocument();
                }}
                minHeight="400px"
                testId="document-view-error"
            />
        );
    }

    return (
        <div
            style={{
                margin: '0 auto',
                maxWidth: '660px',
            }}
        >
            <div
                style={{
                    position: 'relative',
                    width: '100%',
                    marginBottom: '1.5rem',
                }}
            >
                {documentStore.isLoading ? (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '300px',
                            flexDirection: 'column',
                            gap: '1rem',
                        }}
                    >
                        <ProgressSpinner
                            style={{
                                width: '40px',
                                height: '40px',
                            }}
                            strokeWidth="4"
                            animationDuration="1s"
                        />
                        <div
                            style={{
                                color: '#6c757d',
                                fontSize: '1rem',
                                textAlign: 'center',
                            }}
                        >
                            In Arbeit...
                        </div>
                    </div>
                ) : cleanHtml ? (
                    <div
                        style={{
                            width: '100%',
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            backgroundColor: '#ffffff',
                            padding: '1.5rem',
                            minHeight: '400px',
                            textAlign: 'left',
                            lineHeight: '1.6',
                            fontSize: '0.95rem',
                            color: '#495057',
                        }}
                        dangerouslySetInnerHTML={{ __html: cleanHtml }}
                    />
                ) : (
                    <div
                        style={{
                            padding: '2rem',
                            textAlign: 'center',
                            color: '#6c757d',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '400px',
                        }}
                    >
                        <i
                            className="pi pi-file-o"
                            style={{
                                fontSize: '2rem',
                                marginBottom: '1rem',
                                display: 'block',
                            }}
                        />
                        <p style={{ margin: 0, fontSize: '1rem' }}>Kein Dokument verfügbar</p>
                    </div>
                )}
            </div>

            {documentStore?.pdfSignedUrl && (
                <div
                    style={{
                        padding: '1rem',
                        backgroundColor: '#f8fff8',
                        borderRadius: '8px',
                        border: '1px solid #4CAF5020',
                        textAlign: 'center',
                    }}
                >
                    <i
                        className="pi pi-check-circle"
                        style={{
                            fontSize: '1.5rem',
                            color: '#4CAF50',
                            marginBottom: '0.5rem',
                            display: 'block',
                        }}
                    />
                    <a
                        href={documentStore.pdfSignedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            fontSize: '0.95rem',
                            color: '#4CAF50',
                            textDecoration: 'underline',
                            fontWeight: '600',
                        }}
                    >
                        Signiertes PDF ansehen
                    </a>
                </div>
            )}

            <MatenSign
                visible={documentStore.showSignDialog}
                onHide={() => documentStore.closeSignDialog()}
                onSave={(signatureDataUrl, accuracy, width, height) =>
                    documentStore.signSignature(signatureDataUrl, accuracy, width, height)
                }
                title="Dokument unterzeichnen"
                accuracy={documentStore.documentSignatureAccuracy || undefined}
            />
        </div>
    );
});

export default DocumentView;
